import { forwardRef } from 'react'

import { Currency } from '@/constants/currency'
import { cn } from '@/lib/utils'

import {
  AnimatedFormLabel,
  Card,
  FormControl,
  MoneyInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '../ui'

import { CurrencyFlag } from './CurrencyFlag'

const AVAILABLE_CURRENCIES = [Currency.USDC, Currency.EURC]

function getInputCurrency(currency?: Currency) {
  switch (currency) {
    case Currency.EURC:
      return Currency.EUR

    case Currency.USDC:
      return Currency.USD

    default:
      return Currency.USD
  }
}

export interface AmountInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  currency?: Currency
  disabled?: boolean
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  onCurrencyChange?: (currency: Currency) => void
}

export const AmountInput = forwardRef<HTMLInputElement, AmountInputProps>(
  (
    {
      currency,
      disabled,
      placeholder,
      value,
      onChange,
      onCurrencyChange,
      ...props
    },
    ref,
  ) => {
    return (
      <Card
        size="input"
        className={cn('flex', disabled && 'cursor-not-allowed opacity-50')}
      >
        {onCurrencyChange ? (
          <Select
            value={currency}
            onValueChange={onCurrencyChange}
            disabled={disabled}
          >
            <SelectTrigger className="text-neutral-gray-900 hover:text-neutral-gray-800 w-28 p-0 hover:transition-colors focus:ring-0 focus:ring-white focus:ring-offset-0">
              <CurrencyFlag currency={currency} />
            </SelectTrigger>

            <SelectContent>
              {AVAILABLE_CURRENCIES.map((currency) => (
                <SelectItem
                  className="h-[54px]"
                  key={currency}
                  value={currency}
                >
                  <CurrencyFlag currency={currency} />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <CurrencyFlag currency={currency} />
        )}

        <FormControl>
          <MoneyInput
            disabled={disabled}
            currency={getInputCurrency(currency)}
            placeholder={placeholder}
            className="text-right"
            value={value}
            onChange={onChange}
            {...props}
            ref={ref}
          />
        </FormControl>

        <AnimatedFormLabel align="end">{placeholder}</AnimatedFormLabel>
      </Card>
    )
  },
)

AmountInput.displayName = 'AmountInput'
